import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { api, fileUrl } from '../lib/api';
import { ReviewBadge, SourceNote } from './DataBadge';
import { Skeleton } from './ui/skeleton';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

export const PIE_COLORS = ['#1a2744', '#C9A84C', '#3d6a8a', '#2d7a55', '#b3542d'];

// Shared chart renderer used by the City Data dashboard AND Newsroom chart
// blocks. All charts are mobile-safe: ResponsiveContainer for graphs and
// scroll-safe containers for tables.
export const DatasetChart = ({ ds }) => {
  const points = ds.data_points || [];
  const hasSecond = points.some((p) => p.value2 !== undefined && p.value2 !== null && p.value2 !== '');
  const type = ds.chart_type === 'timeline' ? 'line' : ds.chart_type === 'comparison_cards' ? 'stat_cards' : ds.chart_type;

  if (type === 'line') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} width={44} />
          <Tooltip />
          <Line type="monotone" dataKey="value" stroke="#1a2744" strokeWidth={3} dot={{ fill: '#C9A84C', r: 5 }} />
          {hasSecond && <Line type="monotone" dataKey="value2" stroke="#C9A84C" strokeWidth={2.5} dot={{ fill: '#1a2744', r: 4 }} />}
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (type === 'area') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} width={44} />
          <Tooltip />
          <Area type="monotone" dataKey="value" stroke="#1a2744" strokeWidth={2.5} fill="#1a2744" fillOpacity={0.12} />
        </AreaChart>
      </ResponsiveContainer>
    );
  }
  if (type === 'pie' || type === 'doughnut') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={points} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={80}
            innerRadius={type === 'doughnut' ? 48 : 0}
            label={({ label, value }) => `${label}: ${value}`}>
            {points.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (type === 'table') {
    return (
      <div className="h-full overflow-y-auto overflow-x-auto" data-testid="dataset-table">
        <table className="w-full text-sm min-w-[280px]">
          <thead>
            <tr className="bg-[#1a2744] text-white">
              <th className="text-left font-bold px-3 py-2 rounded-tl-lg">Item</th>
              <th className="text-right font-bold px-3 py-2 rounded-tr-lg">Value{ds.unit ? ` (${ds.unit})` : ''}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((p, i) => (
              <tr key={i} className={i % 2 ? 'bg-slate-50' : 'bg-white'}>
                <td className="px-3 py-2 text-slate-700 border-b border-slate-100">{p.label}</td>
                <td className="px-3 py-2 text-right font-bold text-[#1a2744] border-b border-slate-100">{p.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  if (type === 'stat_cards') {
    return (
      <div className="h-full overflow-y-auto grid grid-cols-2 gap-2 content-start" data-testid="dataset-stat-cards">
        {points.map((p, i) => (
          <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-xl font-black text-[#1a2744] leading-none">{p.value}{ds.unit ? <span className="text-xs font-bold text-slate-400 ml-0.5">{ds.unit}</span> : ''}</p>
            <p className="text-[11px] font-semibold text-slate-500 mt-1 leading-tight">{p.label}</p>
          </div>
        ))}
      </div>
    );
  }
  if (type === 'stacked_bar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={points}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} width={44} />
          <Tooltip />
          <Legend />
          <Bar dataKey="value" stackId="a" fill="#1a2744" name={ds.series_label || 'Value'} />
          {hasSecond && <Bar dataKey="value2" stackId="a" fill="#C9A84C" radius={[4, 4, 0, 0]} name={ds.series2_label || 'Value 2'} />}
        </BarChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={points}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="label" tick={{ fontSize: 12 }} />
        <YAxis tick={{ fontSize: 12 }} width={44} />
        <Tooltip />
        <Bar dataKey="value" fill="#1a2744" radius={[4, 4, 0, 0]} />
        {hasSecond && <Bar dataKey="value2" fill="#C9A84C" radius={[4, 4, 0, 0]} />}
      </BarChart>
    </ResponsiveContainer>
  );
};

// A single chart block inside a Newsroom article.
// type: 'dataset' (embed an existing published City Data chart),
//       'inline'  (simple chart defined in the article editor),
//       'image'   (uploaded chart image / safe embed screenshot)
// Merge a linked dataset with any per-article overrides set on the block
const mergeDatasetBlock = (block, dataset) => (
  block.type === 'dataset' && dataset
    ? { ...dataset, ...Object.fromEntries(Object.entries(block).filter(([k, v]) => v !== '' && v !== null && v !== undefined && !['type', 'dataset_id'].includes(k))) }
    : block
);

const ChartBlockMedia = ({ block, state, merged }) => {
  if (state === 'loading') return <Skeleton className="h-56 w-full mt-3 rounded-xl" />;
  if (block.type === 'image') {
    return <img src={fileUrl(block.image_url)} alt={block.alt_text || block.title || 'Chart'} className="w-full h-auto rounded-xl mt-3 border border-slate-100" data-testid="chart-block-image" />;
  }
  return (
    <div className="h-56 sm:h-64 mt-3">
      <DatasetChart ds={merged} />
    </div>
  );
};

const ChartBlockFooter = ({ block, merged }) => (
  <>
    {block.description && <figcaption className="text-sm text-slate-600 mt-3 leading-relaxed break-words">{block.description}</figcaption>}
    {block.data_notes && <p className="text-[11px] text-slate-500 mt-2 leading-relaxed break-words [overflow-wrap:anywhere]" data-testid="chart-block-notes">Data notes: {block.data_notes}</p>}
    <SourceNote
      sourceName={block.source_name || merged.source_name}
      sourceUrl={block.source_url || merged.source_url}
      lastUpdated={block.last_updated || merged.last_updated}
      testId="chart-block-source-note"
    />
    {block.type === 'dataset' && (
      <Link to="/city-data" className="inline-block mt-2 text-xs font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="chart-block-citydata-link">
        Explore this chart on the City Data dashboard
      </Link>
    )}
  </>
);

const ArticleChartBlock = ({ block }) => {
  const [dataset, setDataset] = useState(null);
  const [state, setState] = useState(block.type === 'dataset' ? 'loading' : 'ready');

  useEffect(() => {
    if (block.type === 'dataset' && block.dataset_id) {
      api.get(`/datasets/${block.dataset_id}`)
        .then((r) => { setDataset(r.data); setState('ready'); })
        .catch(() => setState('unavailable'));
    } else if (block.type === 'dataset') {
      setState('unavailable');
    }
  }, [block.type, block.dataset_id]);

  if (block.public === false) return null;
  if (state === 'unavailable') return null; // linked chart unpublished — never show stale data

  const merged = mergeDatasetBlock(block, dataset);

  return (
    <figure className="my-6 not-prose min-w-0 max-w-full" data-testid="article-chart-block" role="img" aria-label={block.alt_text || block.title || 'Chart'}>
      <Card className="overflow-hidden max-w-full">
        <CardContent className="p-4 sm:p-5 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap min-w-0">
            {(merged.title || block.title) && <p className="font-serif font-bold text-[#1a2744] leading-snug min-w-0 break-words" data-testid="chart-block-title">{block.title || merged.title}</p>}
            <ReviewBadge status={block.review_status || merged.review_status} />
          </div>
          {block.subtitle && <p className="text-xs text-slate-500 mt-0.5 break-words">{block.subtitle}</p>}
          <ChartBlockMedia block={block} state={state} merged={merged} />
          <ChartBlockFooter block={block} merged={merged} />
        </CardContent>
      </Card>
    </figure>
  );
};

export const ArticleChartBlocks = ({ blocks }) => {
  const visible = (blocks || []).filter((b) => b && b.public !== false);
  if (!visible.length) return null;
  return (
    <div data-testid="article-chart-blocks">
      {visible.map((b, i) => <ArticleChartBlock key={b.id || i} block={b} />)}
    </div>
  );
};
